import React, { useEffect, useMemo, useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { List, Container, ListItem, ListItemText } from '@material-ui/core';
import { useHistory, withRouter } from 'react-router-dom';
import { useSelector } from 'react-redux';
import rou from '../../../router';
import {
  AdmissionIcon,
  BedStatusIcon,
  DutyAssignIcon,
  EHandoverIcon,
  EquipmentIcon,
  HomeIcon,
  NotifyIcon,
  PatientSurveyIcon,
  SettingIcon,
  TeleVisitIcon,
  WardDriveIcon,
} from '../../Icon';
import { classNames } from '../../../utils';
import { i18n } from '../../../language';
import { NavMenuE } from '../../../comm/RoleMap';
import { authNameE } from '../../../utils/userFuncs';
import { goToWardDrivePage } from '../../../utils/wardDrive';
import { envP } from '../../../utils/environments';
import { verFunc } from '../../../utils/version';
import SystemDisplayMessage from '../../SystemDisplayMessage';
import { clearRedirectUrl } from '../../../utils/check';
import { userRoleFunc } from '../../../utils/userRole';
import { CUBICLE_VIEW, CUBICLE_VIEW2 } from '../../../redux/constant';

const useStyles = makeStyles((theme) => ({
  root: {
    position: 'fixed',
    left: 0,
    top: 0,
    bottom: 0,
    width: '6.25rem',
    padding: '4.438rem 0rem 23.438rem 0.313rem',
    backgroundColor: ({ env }) => {
      if (env === envP.PPS) {
        return '#f18740';
      }
      return theme.palette.primary.main;
    },
    zIndex: 999,
    overflowY: 'auto',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },
  item: {
    width: '5.938rem',
    height: '5.125rem',
    flexDirection: 'column',
    justifyContent: 'center',
    borderRadius: '1rem 0px 0px 1rem',
    color: '#fff',
    padding: 0,
    marginBottom: '1.1rem',
  },
  selection: {
    background: '#fff',
    boxShadow: '0 3px 6px 0 rgba(0, 0, 0, 0.08)',
    color: theme.palette.primary.main,
    '&:hover': {
      background: '#fff',
    },
  },
  link: {
    textDecoration: 'none',
  },
  homeIcon: {
    width: '1.375rem',
    height: '1.375rem',
  },
  eHandoverIcon: {
    width: '1.188rem',
    height: '1.375rem',
  },
  notificationIcon: {
    width: '1.25rem',
    height: '1.375rem',
  },
  bedStatusIconIcon: {
    width: '1.312rem',
    height: '1.375rem',
  },
  dutyassignIcon: {
    width: '1.125rem',
    height: '1.25rem',
  },
  settingIcon: {
    width: '1.25rem',
    height: '1.25rem',
  },
  equipmentIcon: {
    width: '1.375rem',
    height: '1.438rem',
  },
  televisitIcon: {
    width: '1.375rem',
    height: '1.438rem',
  },
  wardDriveIcon: {
    width: '1.95rem',
    height: '1.625rem',
  },
  admissionIcon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  patientSurveyIcon: {
    width: '1.5rem',
    height: '1.5rem',
  },
  fontStyle: {
    flex: 0,
    margin: '0',
    '& span': {
      fontSize: '0.75rem',
      fontFamily: 'NotoSans-Medium',
      fontWeight: '500',
      lineHeight: 'unset',
      marginTop: '0.438rem',
      textAlign: 'center',
    },
  },
  version: {
    textAlign: 'center',
    bottom: 0,
    width: '100% ',
    height: '32px ',
    color: '#fff',
    position: 'relative',
  },
}));

function Nav(props) {
  const userRole = useSelector((state) => state.userRole);
  const loginInfo = useSelector((state) => state.loginInfo);
  const rouList = useSelector((state) => state.rouList);
  const roleMap = useSelector((state) => state.roleMap);
  const hospitalWardList = useSelector((state) => state.hospitalWardList);
  let [version, setVersion] = useState(null);
  const { defaultViewType } = useSelector((state) => state.bedStatusView);
  const sso = useSelector((state) => state.ssoReducer);
  const env = useSelector((state) => state.envReducer);
  const classes = useStyles({env});


  const allNavData = useMemo(
    () => [
      {
        id: authNameE.home,
        idName: 'wp_nav_home',
        icon: HomeIcon,
        title: 'NAV_HOME',
        path: rou.homeRou.path,
        iconStyle: classes.homeIcon,
        moduleCode: NavMenuE.home,
      },
      {
        id: authNameE.notification,
        idName: 'wp_nav_notification',
        icon: NotifyIcon,
        title: 'NAV_NOTIFICATION',
        path: rou.notificationRou.path,
        iconStyle: classes.notificationIcon,
        moduleCode: NavMenuE.notification,
      },
      {
        id: authNameE.eHandover,
        idName: 'wp_nav_eHandover',
        icon: EHandoverIcon,
        title: 'NAV_EHANDOVER',
        path: rou.ehandoverRou.path,
        iconStyle: classes.eHandoverIcon,
        moduleCode: NavMenuE.eHandover,
      },
      {
        id: authNameE.bedStatus,
        idName: 'wp_nav_bedStatus',
        icon: BedStatusIcon,
        title: 'NAV_BED_STATUS',
        path: [CUBICLE_VIEW, CUBICLE_VIEW2].includes(defaultViewType)
          ? rou.bedstatusNewRou.path
          : rou.bedstatusRou.path,
        otherPath: ![CUBICLE_VIEW, CUBICLE_VIEW2].includes(defaultViewType)
          ? rou.bedstatusRou.path
          : rou.bedstatusNewRou.path,
        iconStyle: classes.bedStatusIconIcon,
        moduleCode: NavMenuE.bedStatus,
      },
      {
        id: authNameE.dutyAssign,
        idName: 'wp_nav_dutyAssign',
        icon: DutyAssignIcon,
        title: 'NAV_DUTY_ASSIGN',
        path: rou.dutyassignRou.path,
        iconStyle: classes.dutyassignIcon,
        moduleCode: NavMenuE.dutyAssign,
      },
      {
        id: authNameE.equipment,
        idName: 'wp_nav_equipment',
        icon: EquipmentIcon,
        title: 'NAV_EQUIPMENT',
        path: rou.equipmentRou.path,
        iconStyle: classes.equipmentIcon,
        moduleCode: NavMenuE.equipment,
      },
      {
        id: authNameE.televisit,
        idName: 'wp_nav_televisit',
        icon: TeleVisitIcon,
        title: 'NAV_TELEVISIT',
        path: rou.televisitRou.path,
        iconStyle: classes.televisitIcon,
        moduleCode: NavMenuE.televisit,
      },
      {
        id: authNameE.wardDrive,
        idName: 'wp_nav_wardDrive',
        icon: WardDriveIcon,
        title: 'NAV_WARD_DRIVE',
        path: rou.wardDriveRou.path,
        iconStyle: classes.wardDriveIcon,
        moduleCode: NavMenuE.wardDrive,
      },
      {
        id: authNameE.admission,
        idName: 'wp_nav_admission',
        icon: AdmissionIcon,
        title: 'NAV_ADMISSION',
        path: rou.admissionRou.path,
        iconStyle: classes.admissionIcon,
        moduleCode: NavMenuE.admission,
      },
      {
        id: authNameE.patient_survey,
        idName: 'wp_nav_patient_survey',
        icon: PatientSurveyIcon,
        title: 'NAV_PATIENT',
        path: rou.patientSurveyRou.path,
        iconStyle: classes.patientSurveyIcon,
        moduleCode: NavMenuE.patient,
      },
      {
        id: authNameE.setting,
        idName: 'wp_nav_setting',
        icon: SettingIcon,
        title: 'NAV_SETTING',
        path: rou.settingRou.path,
        iconStyle: classes.settingIcon,
        moduleCode: NavMenuE.setting,
      },
    ],
    [defaultViewType]
  );
  const history = useHistory();

  const filterNavData = (navData) => {
    return navData.filter((item) => {
      if (item?.moduleCode === NavMenuE.admission) {
        // logout的状态也需要能看到admission
        return roleMap[item.id] && (userRoleFunc?.getIsShowAdmission() || !loginInfo?.flag);
      }
      return roleMap[item.id];
    });
  };

  const navData = useMemo(() => {
    if (Array.isArray(userRole) && loginInfo.flag) {
      return filterNavData(allNavData);
    } else if (Array.isArray(hospitalWardList)) {
      return filterNavData(allNavData);
    }
  }, [rouList]);

  function selectBtn(path, listData) {
    let index = -1;
    listData?.some((item, iIndex) => {
      if (isPathMatched(path, item)) {
        index = iIndex;
        return true;
      }
      return false;
    });
    return index;
  }

  // 新增的辅助函数
  const isPathMatched = (path, item) => {
    const re = new RegExp(`^(${item.path})|(${item.otherPath})`);
    return re.test(path);
  };

  let [NavIndex, setNavIndex] = useState(selectBtn(props.location.pathname, navData));

  useEffect(() => {
    const index = selectBtn(props.location.pathname, navData);
    if (index > -1) {
      setNavIndex(index);
    }
  }, [rouList]);

  useEffect(() => {
    (async () => {
      setVersion(await verFunc.getVersion());
    })();
  }, []);

  useEffect(() => {
    clearRedirectUrl();
  }, []);

  return (
    !sso && (
      <Container maxWidth="lg" className={classes.root}>
        <List component="nav" aria-labelledby="nested-list-subheader">
          {navData?.map((item, index) => {
            const NavIcon = item.icon;
            return (
              <div className={classes.link} key={item.id}>
                <ListItem
                  id={item.idName}
                  button
                  className={index === NavIndex ? classNames(classes.item, classes.selection) : classes.item}
                  onClick={() => {
                    if (item?.moduleCode === NavMenuE.wardDrive) {
                      goToWardDrivePage();
                      return;
                    }

                    history.push(item.path);
                  }}
                >
                  <NavIcon className={item.iconStyle} />
                  <ListItemText primary={i18n.t(item.title)} className={classes.fontStyle} />
                </ListItem>
              </div>
            );
          })}
        </List>
        {version ? (
          <div className={classes.version}>
            <p>v{version}</p>
            <SystemDisplayMessage displayKey="RELEASE_NOTE" />
          </div>
        ) : null}
      </Container>
    )
  );
}

export default withRouter(Nav);
